import { useEffect, useMemo, useState } from 'react';

/**
 * Plumbing shared by the Platform screens (approvals, audit trail, inbox).
 * Nothing here renders — the screens own their markup.
 */

/** Surface the API's own message (§3 envelope) rather than a generic failure. */
export function apiMessage(error: unknown, fallback: string): string {
  const payload = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(payload)) return payload.filter(Boolean).join(' · ');
  if (typeof payload === 'string' && payload.trim()) return payload;
  return fallback;
}

/** The HTTP status the API answered with, so a 403 can be told from a 409. */
export function apiStatus(error: unknown): number | null {
  return (error as { response?: { status?: number } })?.response?.status ?? null;
}

export function formatDateTime(value: string | Date | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'medium' }).format(date);
}

/** Drops empty strings so a blank filter never reaches the API as `?status=`. */
export function useQueryParams(source: Record<string, unknown>): Record<string, unknown> {
  const key = JSON.stringify(source);
  return useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const [name, value] of Object.entries(JSON.parse(key) as Record<string, unknown>)) {
      if (value === '' || value === null || value === undefined) continue;
      out[name] = value;
    }
    return out;
    // `source` is compared by value through `key`
  }, [key]);
}

/**
 * A clock that re-renders its host on an interval, so an expiry countdown ticks
 * down without refetching the queue.
 */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export interface Countdown {
  /** Milliseconds left; negative once the deadline has passed. */
  remainingMs: number;
  expired: boolean;
  /** `h:mm:ss` above an hour, `mm:ss` below it. Empty when there is no deadline. */
  label: string;
  /** Under five minutes left — the queue paints these to be noticed. */
  urgent: boolean;
}

/** Formats `expiresAt` against `now` for the approval queue's expiry column. */
export function countdown(expiresAt: string | null | undefined, now: number): Countdown | null {
  if (!expiresAt) return null;
  const deadline = new Date(expiresAt).getTime();
  if (Number.isNaN(deadline)) return null;

  const remainingMs = deadline - now;
  if (remainingMs <= 0) return { remainingMs, expired: true, label: '', urgent: true };

  const seconds = Math.floor(remainingMs / 1000);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  return {
    remainingMs,
    expired: false,
    label: h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`,
    urgent: remainingMs < 5 * 60_000,
  };
}

/** Prisma serializes `Decimal` as a string; quantities carry noise zeros. */
export function trimDecimal(value: unknown): string {
  const text = String(value ?? '').trim();
  if (!text) return '0';
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
}

interface Named {
  firstName?: string | null;
  lastName?: string | null;
}

/** "Firstname Lastname", or a dash when the actor is a background job. */
export function personName(person: Named | null | undefined): string {
  if (!person) return '—';
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return name || '—';
}
